import type { AppTranslations } from './types'

export const it = {
  characteristics: {
    common: {
      na: 'N/D',
      no: 'No',
      source: 'Fonte',
      sourceCalculated: 'Calcolato',
      years: 'anni',
      yes: 'Sì',
    },
    developmentPotential: {
      tooltips: {
        far: {
          calculation: 'Superficie lorda di pavimento totale divisa per la superficie del terreno. Esempio: edificio di 1000 m² su un terreno di 500 m² = IS di 2.0',
          categories: [
            {
              description: 'Rurale, case unifamiliari su grandi lotti',
              label: 'Bassa densità',
              range: 'IS 0.1-0.5',
            },
            {
              description: 'Suburbano, case a schiera',
              label: 'Densità media',
              range: 'IS 0.5-1.5',
            },
            {
              description: 'Urbano, edifici residenziali di media altezza',
              label: 'Alta densità',
              range: 'IS 1.5-3.0',
            },
            {
              description: 'Centro città, edifici a torre',
              label: 'Densità molto alta',
              range: 'IS >3.0',
            },
          ],
          importance: "L'IS è l'indicatore principale per comprendere l'intensità di sviluppo e l'efficienza dell'uso del suolo. Un IS più elevato indica un uso più intensivo del terreno e generalmente valori immobiliari più elevati per metro quadrato di terreno.",
          limitations: "Le normative sull'IS variano a seconda della zona. Consultare i documenti del piano di utilizzazione locale per l'IS massimo consentito nella propria zona.",
          shortDescription: 'Rapporto tra la superficie lorda di pavimento totale e la superficie del fondo',
          source: 'Calcolato dal Registro federale degli edifici e delle abitazioni (REA) e dal registro fondiario',
          title: 'Indice di sfruttamento (IS)',
        },
        floorPotential: {
          calculation: 'Differenza tra il numero massimo di piani consentito (secondo il piano di utilizzazione locale) e il numero attuale di piani',
          importance: "I piani aggiuntivi rappresentano il percorso più diretto per aumentare la capacità dell'edificio e il valore immobiliare. Ogni piano aggiuntivo può aggiungere una superficie utile significativa e un potenziale di ricavi locativi o vendita.",
          limitations: "L'aggiunta di piani richiede un'analisi strutturale, una valutazione della capacità delle fondazioni e può comportare requisiti per ascensori, adeguamenti antincendio e posti auto aggiuntivi. I costi possono essere considerevoli.",
          shortDescription: 'Confronto tra il numero attuale di piani e il massimo consentito dalla zonizzazione',
          source: 'Normative del piano di utilizzazione locale (PUL) e Registro federale degli edifici e delle abitazioni (REA)',
          title: 'Potenziale del numero di piani',
        },
        heightPotential: {
          calculation: "Differenza tra l'altezza massima consentita (secondo il piano di utilizzazione locale) e l'altezza attuale dell'edificio",
          importance: "Comprendere il potenziale di altezza è fondamentale per valutare le possibilità di espansione verticale, il valore di sviluppo immobiliare e la conformità alle normative locali. Un'altezza aggiuntiva può aumentare significativamente la superficie utile e il valore dell'immobile.",
          limitations: "Le restrizioni di altezza massima possono essere soggette ad altri fattori come le distanze dai confini, gli studi sulle ombre, i vincoli del patrimonio o l'opposizione del vicinato. Si raccomanda una consulenza professionale in materia di pianificazione.",
          shortDescription: "Confronto tra l'altezza attuale e l'altezza massima consentita dalla zonizzazione locale",
          source: 'Normative del piano di utilizzazione locale (PUL) e Registro federale degli edifici e delle abitazioni (REA)',
          title: "Potenziale di altezza dell'edificio",
        },
      },
    },
    header: {
      subtitle: 'Informazioni dettagliate su questo edificio',
      title: "Caratteristiche dell'edificio",
    },
    neighborhood: {
      ageRanges: {
        '1919_1945': '1919-1945',
        '1946_1960': '1946-1960',
        '1961_1970': '1961-1970',
        '1971_1980': '1971-1980',
        '1981_1990': '1981-1990',
        '1991_2000': '1991-2000',
        '2001_2010': '2001-2010',
        '2011_2020': '2011-2020',
        post2020: 'Dopo il 2020',
        pre1919: 'Prima del 1919',
      },
      badges: {
        buildingOutlier: 'Edificio atipico',
      },
      cards: {
        ageDistribution: {
          title: "Distribuzione dell'età degli edifici",
        },
        analysis: {
          tooltip: {
            source: 'REA, Popety.io',
          },
        },
        areaComparison: {
          title: 'Confronto superficie',
          tooltip: {
            calculation: 'Confronta la superficie del piano terra di questo edificio con le superfici media e massima degli edifici vicini',
            description: 'Come la superficie di questo edificio si confronta con gli edifici circostanti',
            importance: "La superficie dell'edificio rispetto ai vicini indica l'intensità d'uso del suolo, la densità di sviluppo e se l'edificio è tipico per la zona.",
            limitations: "La superficie da sola non riflette la dimensione totale dell'edificio. Controllare il numero di piani e la superficie lorda per una valutazione completa.",
            title: 'Confronto superficie con i vicini',
          },
        },
        cohesionBreakdown: {
          title: 'Ripartizione della coesione',
          tooltip: {
            calculation: 'Media ponderata dei punteggi di coesione temporale (40%), fisica (30%), tipologica (20%) e di densità (10%)',
            description: 'Mostra quanto è omogeneo il quartiere su più dimensioni',
            importance: 'Un punteggio di coesione elevato indica un quartiere ben definito con caratteristiche edilizie coerenti',
            title: 'Ripartizione della coesione',
          },
        },
        comparativeAnalysis: {
          title: 'Analisi comparativa',
        },
        conformityBreakdown: {
          title: "Ripartizione della conformità dell'edificio",
          tooltip: {
            calculation: 'Confronta le caratteristiche di questo edificio con il profilo tipico del quartiere',
            description: 'Mostra quanto bene questo edificio specifico corrisponde al modello del quartiere',
            importance: "Una bassa conformità in un quartiere coeso indica un edificio atipico, che può influenzare la valutazione, le restrizioni di sviluppo o l'armonia architettonica.",
            title: "Ripartizione della conformità dell'edificio",
          },
        },
        heightComparison: {
          title: 'Confronto altezza',
          tooltip: {
            calculation: "Confronta l'altezza totale di questo edificio con le altezze media e massima degli edifici vicini entro un raggio definito (tipicamente 100-200m)",
            description: "Come l'altezza di questo edificio si confronta con gli edifici circostanti",
            importance: "L'altezza dell'edificio rispetto ai vicini influisce su viste, esposizione solare, privacy, armonia architettonica e conformità alle normative urbanistiche locali.",
            limitations: 'Confronto basato sugli edifici entro il raggio di ricerca. La topografia locale e le differenze di altitudine del terreno non sono riflesse in questa metrica.',
            title: 'Confronto altezza con i vicini',
          },
        },
        neighborhoodAnalysis: {
          title: "Analisi del quartiere e dell'edificio",
          tooltip: {
            calculation: "La coesione del quartiere misura quanto sono simili gli edifici circostanti. La conformità dell'edificio misura quanto bene QUESTO edificio corrisponde al modello del quartiere.",
            description: 'Analizza sia la coesione del quartiere che quanto bene questo edificio si inserisce nel quartiere rilevato',
            importance: "Un'alta coesione con bassa conformità indica un edificio atipico in un quartiere coeso, che può influenzare la valutazione e il potenziale di sviluppo.",
            title: "Analisi del quartiere e dell'edificio",
          },
        },
        quartierProfile: {
          title: 'Profilo del quartiere',
        },
        volumeComparison: {
          title: 'Confronto volume',
          tooltip: {
            calculation: 'Confronta il volume totale chiuso di questo edificio con i volumi medio e massimo degli edifici vicini',
            description: 'Come il volume totale di questo edificio si confronta con gli edifici circostanti',
            importance: "Il volume dell'edificio è un indicatore chiave della massa totale e dell'intensità di sviluppo. Usato per la modellazione energetica e la pianificazione della capacità HVAC.",
            limitations: 'I calcoli del volume possono utilizzare ipotesi geometriche semplificate. Il volume interno effettivo può variare a causa della geometria del tetto e delle disposizioni interne.',
            title: 'Confronto volume con i vicini',
          },
        },
      },
      chart: {
        areaAxis: 'Superficie (m²)',
        buildingsAxis: 'Edifici',
        heightAxis: 'Altezza (m)',
        neighborhoodAvg: 'Media quartiere',
        neighborhoodMax: 'Max. quartiere',
        thisBuilding: 'Questo edificio',
        volumeAxis: 'Volume (m³)',
      },
      charts: {
        area: {
          source: 'Popety.io, REA',
        },
        height: {
          source: 'Popety.io, REA',
        },
        volume: {
          source: 'Popety.io, REA',
        },
      },
      cohesion: {
        density: {
          label: 'Densità (10%)',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: 'Basato sul numero di edifici per chilometro quadrato. Una densità più alta suggerisce un quartiere urbano.',
            description: 'Misura la densità di sviluppo',
            title: 'Coesione di densità',
          },
        },
        physical: {
          label: 'Coesione fisica (30%)',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: 'Basato sulla similarità di altezza, superficie e numero di piani. Punteggi più alti indicano caratteristiche edilizie uniformi.',
            description: 'Misura la similarità architettonica',
            title: 'Coesione fisica',
          },
        },
        temporal: {
          label: 'Coesione temporale (40%)',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: 'Basato sulla similarità degli anni di costruzione. Punteggi più alti indicano edifici della stessa epoca.',
            description: 'Misura se gli edifici sono stati costruiti nello stesso periodo',
            title: 'Coesione temporale',
          },
        },
        typological: {
          label: 'Coesione tipologica (20%)',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: 'Basato sulla prevalenza di categorie di edifici simili (es. monofamiliare, plurifamiliare, ecc.)',
            description: 'Misura la similarità dei tipi di edificio',
            title: 'Coesione tipologica',
          },
        },
      },
      comparison: {
        aboveAverage: 'Sopra la media',
        belowAverage: 'Sotto la media',
        similar: 'Simile',
      },
      conformity: {
        fitsWell: 'Si adatta bene',
        outlier: 'Atipico',
        partialFit: 'Integrazione parziale',
        physicalMatch: {
          label: 'Corrispondenza fisica (40%)',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: 'Media della similarità di altezza, superficie e numero di piani. ±20-30% = alta corrispondenza',
            description: "Quanto bene le dimensioni e l'altezza di questo edificio corrispondono al quartiere tipico",
            title: 'Corrispondenza fisica',
          },
        },
        temporalMatch: {
          label: 'Corrispondenza temporale (40%)',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: "Confronta l'anno dell'edificio con l'anno tipico. ±5 anni = 100%, ±10 anni = 75%, ±20 anni = 50%",
            description: "Quanto bene l'anno di costruzione di questo edificio corrisponde al periodo tipico del quartiere",
            title: 'Corrispondenza temporale',
          },
        },
        tooltip: {
          source: 'Popety.io',
        },
        typologicalMatch: {
          label: 'Corrispondenza tipologica (20%)',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: '100 se stessa categoria di tipico, 0 se diverso',
            description: 'Se il tipo di questo edificio corrisponde alla categoria dominante del quartiere',
            title: 'Corrispondenza tipologica',
          },
        },
      },
      errors: {
        loadFailed: 'Caricamento dati quartiere non riuscito',
      },
      loading: 'Caricamento dati quartiere...',
      metrics: {
        areaVsAverage: {
          label: 'Superficie vs media',
          tooltip: {
            calculation: 'Differenza percentuale dalla superficie media degli edifici entro il raggio di ricerca',
            description: 'Come la superficie di questo edificio si confronta con gli edifici vicini',
            importance: "Indica se l'edificio è tipico o insolitamente grande/piccolo per la zona. Influisce sull'indice e sul potenziale di sviluppo.",
            source: 'Popety.io, REA',
            title: 'Superficie vs media del quartiere',
          },
        },
        avgNeighborAge: {
          label: 'Età media vicini',
          tooltip: {
            calculation: 'Media delle età di tutti gli edifici entro il raggio di ricerca',
            description: 'Età media degli edifici circostanti',
            importance: 'Indica se il quartiere è prevalentemente vecchio (>50 anni), consolidato (20-50 anni) o nuovo (<20 anni).',
            source: 'Popety.io, REA',
            title: 'Età media vicini',
          },
          value: '{{years}} anni',
        },
        buildingConformity: "Conformità dell'edificio",
        buildingDensity: {
          label: 'Densità degli edifici',
          tooltip: {
            calculation: "Conteggio degli edifici entro un raggio definito diviso per l'area di quel raggio",
            description: "Numero di edifici per unità di superficie nell'area circostante",
            importance: "Indica se l'area è urbana, suburbana o rurale. Un'alta densità suggerisce un ambiente urbano con edifici più vicini.",
            source: 'Popety.io, REA',
            title: 'Densità degli edifici',
          },
          value: '{{density}} edifici/km²',
        },
        buildingsAnalyzed: 'Edifici analizzati',
        confidence: 'Affidabilità',
        heightVsAverage: {
          label: 'Altezza vs media',
          tooltip: {
            calculation: "Differenza percentuale dall'altezza media degli edifici entro il raggio di ricerca",
            description: "Come l'altezza di questo edificio si confronta con gli edifici vicini",
            importance: 'Gli edifici significativamente più alti o più bassi dei vicini possono affrontare restrizioni urbanistiche o distinguersi visivamente.',
            source: 'Popety.io, REA',
            title: 'Altezza vs media del quartiere',
          },
        },
        neighborhoodCohesion: 'Coesione del quartiere',
        neighboringBuildings: {
          label: 'Edifici vicini',
          tooltip: {
            calculation: 'Numero di edifici trovati entro il raggio di ricerca selezionato',
            description: 'Conteggio degli edifici entro il raggio di analisi',
            importance: "Più vicini forniscono un migliore confronto statistico. Meno di 10 vicini può indicare un'area rurale o un'affidabilità dei dati limitata.",
            source: 'Popety.io, REA',
            title: 'Edifici vicini',
          },
        },
        volumeVsAverage: {
          label: 'Volume vs media',
          tooltip: {
            calculation: 'Differenza percentuale dal volume medio degli edifici entro il raggio di ricerca',
            description: 'Come il volume di questo edificio si confronta con gli edifici vicini',
            importance: 'Volumi maggiori indicano strutture più sostanziali. Influisce sui costi di riscaldamento e sulla densità di sviluppo.',
            source: 'Popety.io, REA',
            title: 'Volume vs media del quartiere',
          },
        },
      },
      neighboringBuildings: 'Edifici vicini',
      noData: 'Nessun dato di quartiere disponibile',
      notes: {
        differsByPattern: 'Differisce dal modello del quartiere',
        physicalMismatch: 'Le caratteristiche fisiche differiscono dalla media del quartiere',
        temporalMismatch: "L'età dell'edificio differisce dal modello del quartiere",
        title: 'Note',
        typologicalMismatch: 'Il tipo di edificio differisce dallo standard del quartiere',
      },
      profile: {
        description: {
          label: 'Descrizione',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: "Generato dall'anno di costruzione tipico e dalla categoria dominante",
            description: 'Caratterizzazione riassuntiva del quartiere',
            importance: "Fornisce una panoramica rapida del carattere del quartiere (es. 'residenziale anni '60' o 'uso misto anni '80').",
            title: 'Descrizione del quartiere',
          },
        },
        developmentCategories: {
          source: 'Popety.io, REA',
        },
        developmentPattern: {
          categories: {
            mixed: {
              description: 'Coesione moderata, sviluppo vario (punteggio 55-74)',
              label: 'Misto',
            },
            organic: {
              description: 'Bassa coesione, sviluppo diversificato (punteggio <55)',
              label: 'Organico',
            },
            planned: {
              description: 'Alta coesione, sviluppo uniforme (punteggio ≥75)',
              label: 'Pianificato',
            },
          },
          label: 'Modello di sviluppo',
          source: 'Popety.io, REA',
          tooltip: {
            description: 'Classificazione dello stile di sviluppo del quartiere',
            importance: 'Le aree pianificate hanno zonizzazione più rigida. Le aree organiche offrono più flessibilità di ristrutturazione.',
            title: 'Modello di sviluppo',
          },
        },
        typicalArea: {
          label: 'Superficie tipica',
          source: 'Popety.io',
          tooltip: {
            calculation: 'Superficie media degli edifici nel quartiere',
            description: "Impronta dell'edificio rappresentativa per il quartiere",
            importance: 'Indica la copertura tipica del lotto e la scala degli edifici nella zona.',
            title: 'Superficie tipica',
          },
        },
        typicalCategory: {
          label: 'Categoria tipica',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: 'Categoria di edificio più comune tra gli edifici del quartiere',
            description: 'Tipo di edificio dominante nel quartiere',
            importance: 'Definisce il carattere del quartiere: residenziale, commerciale, industriale o misto.',
            title: 'Categoria tipica',
          },
        },
        typicalHeight: {
          label: 'Altezza tipica',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: 'Altezza media degli edifici nel quartiere',
            description: "Altezza dell'edificio rappresentativa per il quartiere",
            importance: "Indica se l'area è prevalentemente bassa (<10m), media (10-25m) o alta (>25m).",
            title: 'Altezza tipica',
          },
        },
        typicalYear: {
          label: 'Anno costruzione tipico',
          source: 'Popety.io, REA',
          tooltip: {
            calculation: 'Anno di costruzione medio degli edifici nel quartiere',
            description: 'Anno di costruzione più rappresentativo per il quartiere',
            importance: "Definisce l'era architettonica e i probabili standard edilizi per la zona.",
            title: 'Anno di costruzione tipico',
          },
        },
      },
      radius: {
        label: 'Raggio di analisi',
        value: '{{value}}m',
      },
      statistics: {
        area: {
          mean: {
            source: 'Popety.io, REA',
            tooltip: {
              calculation: 'Media aritmetica di tutte le superfici degli edifici',
              description: 'Impronta media degli edifici nel quartiere',
              importance: 'Indica la dimensione tipica del lotto. Influenzato da valori anomali.',
              title: 'Superficie media',
            },
          },
          median: {
            source: 'Popety.io, REA',
            tooltip: {
              calculation: '50° percentile delle superfici degli edifici',
              description: 'Valore mediano delle superfici degli edifici del quartiere',
              importance: 'Più rappresentativo della media quando esistono valori anomali.',
              title: 'Superficie mediana',
            },
          },
          range: {
            source: 'Popety.io, REA',
            tooltip: {
              calculation: 'Impronte degli edifici più piccole e più grandi nel quartiere',
              description: 'Superfici minima e massima degli edifici',
              importance: 'Mostra la diversità delle dimensioni. Un ampio intervallo indica scale edilizie miste.',
              title: 'Intervallo di superficie',
            },
          },
          title: 'Superficie',
        },
        floor: {
          floorsRange: '{{min}} - {{max}} piani',
          floorsValue: '{{floors}} piani',
          mean: {
            source: 'Popety.io, REA',
            tooltip: {
              calculation: 'Media aritmetica di tutti i numeri di piani',
              description: 'Numero medio di piani nel quartiere',
              importance: 'Indica la scala tipica degli edifici. Influenzato da valori anomali.',
              title: 'Numero medio di piani',
            },
          },
          median: {
            source: 'Popety.io, REA',
            tooltip: {
              calculation: '50° percentile dei numeri di piani',
              description: 'Valore mediano dei numeri di piani del quartiere',
              importance: 'Più rappresentativo della media quando esistono valori anomali.',
              title: 'Numero mediano di piani',
            },
          },
          range: {
            source: 'Popety.io, REA',
            tooltip: {
              calculation: 'Numeri di piani più bassi e più alti nel quartiere',
              description: 'Numeri di piani minimo e massimo',
              importance: 'Mostra la diversità verticale. Un ampio intervallo indica livelli di altezza misti.',
              title: 'Intervallo del numero di piani',
            },
          },
          title: 'Piani',
        },
        height: {
          mean: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Media aritmetica di tutte le altezze degli edifici',
              description: 'Altezza media degli edifici nel quartiere',
              importance: 'Indica la scala tipica degli edifici. Influenzato da valori anomali.',
              title: 'Altezza media',
            },
          },
          median: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: '50° percentile delle altezze degli edifici',
              description: 'Valore mediano delle altezze degli edifici del quartiere',
              importance: 'Più rappresentativo della media quando esistono valori anomali.',
              title: 'Altezza mediana',
            },
          },
          range: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Altezze degli edifici più basse e più alte nel quartiere',
              description: 'Altezze minima e massima degli edifici',
              importance: 'Mostra la diversità delle altezze. Un ampio intervallo indica sviluppo misto.',
              title: 'Intervallo di altezza',
            },
          },
          title: 'Altezza',
        },
        mean: 'Media',
        median: 'Mediana',
        range: 'Intervallo',
      },
      title: 'Contesto del quartiere',
      tooltips: {
        source: 'Fonte dei dati',
        sourceCalculated: 'Calcolato dai dati del registro degli edifici',
      },
      warnings: {
        outlierDescription: 'Questo edificio non si adatta al modello coeso del quartiere. Potrebbe essere atipico in termini di età, dimensioni o tipo rispetto agli edifici circostanti.',
        outlierTitle: 'Questo edificio è atipico',
      },
    },
    overview: {
      ageCategories: {
        contemporary: {
          description: 'Costruito con metodi e materiali relativamente recenti',
          label: 'Contemporaneo',
          range: '10-30 anni',
        },
        historic: {
          description: 'Edificio storico con potenziale valore patrimoniale e caratteristiche distintive',
          label: 'Storico',
          range: '>50 anni',
        },
        mature: {
          description: 'Edificio consolidato che potrebbe beneficiare di ristrutturazione o modernizzazione',
          label: 'Maturo',
          range: '30-50 anni',
        },
        new: {
          description: 'Costruito di recente con standard moderni ed efficienza energetica',
          label: 'Nuovo',
          range: '<10 anni',
        },
      },
      cards: {
        dimensions: {
          buildingVolume: 'Volume edificio',
          footprintArea: 'Area di base',
          grossFloorArea: 'Superficie lorda',
          groundElevation: 'Elevazione del terreno',
          title: 'Dimensioni fisiche',
          tooltips: {
            elevation: {
              calculation: 'Misurato da dati LiDAR',
              description: "Altezza del livello del suolo nella posizione dell'edificio sopra il livello del mare",
              importance: 'Importante per la valutazione del rischio di inondazione, la pianificazione del drenaggio e la comprensione della topografia locale',
              source: 'Popety.io, swissSURFACE3D',
              title: 'Elevazione del terreno',
            },
            footprintImportance: 'Determina la copertura del terreno ed è cruciale per calcolare il potenziale di sviluppo e la conformità alle normative urbanistiche',
            grossFloorArea: {
              calculation: 'Somma di tutte le superfici dei piani inclusi muri, scale e aree comuni. Calcolato dal registro degli edifici o superficie di base × numero di piani',
              description: 'Superficie totale di tutti i piani, misurata alla faccia esterna delle pareti esterne',
              importance: "Metrica chiave per valutare il potenziale di sviluppo, in particolare il calcolo dell'IBUS",
              limitations: 'Può includere aree non abitabili come corridoi e locali tecnici. La superficie utile netta è tipicamente 10-25% più piccola',
              source: 'REA, Popety.io',
              title: 'Superficie lorda di piano',
            },
            heightCalculation: 'Altezza misurata dal livello del suolo al colmo del tetto utilizzando dati LiDAR e rilievi ufficiali',
            heightImportance: "Critico per la conformità urbanistica, le relazioni di vicinato e la determinazione della classificazione dell'edificio",
            volume: {
              calculation: 'Misurato da dati LiDAR',
              description: "Volume totale racchiuso dell'edificio dalle fondamenta al tetto",
              importance: "Usato per la modellazione energetica, il dimensionamento HVAC e i calcoli della densità di sviluppo, in particolare per l'IM (Indice di Massa)",
              source: 'Popety.io, swissSURFACE3D',
              title: "Volume dell'edificio",
            },
          },
        },
        occupancy: {
          avgFloorArea: 'Sup. media piano',
          avgFloorHeight: 'Alt. media piano',
          buildingCategory: 'Categoria edificio',
          numberOfDwellings: 'Numero di alloggi',
          popetyClassification: 'Classificazione Popety.io',
          primaryUse: 'Uso principale',
          title: 'Occupazione & Tipo',
          tooltips: {
            avgFloorArea: {
              calculation: 'Calcolato dividendo la superficie lorda per il numero di piani',
              description: "La superficie media di ogni piano dell'edificio",
              importance: 'Indica la superficie utile media di un piano',
              source: 'Popety.io',
              title: 'Superficie media del piano',
            },
            avgFloorHeight: {
              calculation: "Calcolato dividendo l'altezza totale dell'edificio per il numero di piani",
              description: "La distanza verticale media tra i piani dell'edificio",
              importance: "Indica l'altezza interpiano tipica e il comfort verticale",
              source: 'Popety.io',
              title: 'Altezza media del piano',
            },
            category: {
              calculation: 'Assegnato dal REA federale in base al loro uso dominante',
              description: "Suddivisione degli edifici secondo la loro destinazione d'uso",
              importance: "Classifica l'edificio secondo l'uso ufficiale per confrontarlo con edifici simili",
              source: 'REA',
              title: "Categoria dell'edificio",
            },
            dwellings: {
              calculation: 'Conteggiato dai registri ufficiali degli edifici',
              description: "Conteggio totale delle singole unità abitative nell'edificio",
              importance: "Conta le abitazioni nell'edificio e orienta occupazione e stima",
              source: 'REA',
              title: 'Numero di alloggi',
            },
            popetyClassification: {
              calculation: "Calcolato da Popety.io sulla base dei dati dell'edificio, dei modelli di utilizzo e dell'analisi di mercato",
              description: "Classificazione dell'edificio determinata dall'algoritmo proprietario di Popety.io",
              importance: 'Fornisce una classificazione standardizzata per scopi di confronto e valutazione',
              title: 'Classificazione Popety.io',
            },
            primaryUse: {
              calculation: 'La classificazione attuale EUROSTAT* suddivide tutte le opere di costruzione in 2 sezioni (edificio / ingegneria civile), 6 divisioni (2/4), 20 gruppi (10/10) e 46 classi (21/25).',
              description: 'Ripartizione degli edifici secondo la classificazione EUROSTAT+',
              importance: "Individua l'uso prevalente, che orienta zona, stima e comparabili",
              source: 'REA',
              title: "Classe dell'edificio",
            },
          },
        },
        occupancyType: {
          title: 'Occupazione e tipo',
        },
        physicalDimensions: {
          title: 'Dimensioni fisiche',
        },
        status: {
          constructionYear: 'Anno di costruzione',
          currentStatus: 'Stato attuale',
          lastRenovation: 'Ultima ristrutturazione',
          minergieCertified: 'Certificato Minergie',
          protectedStatus: 'Stato di protezione',
          title: 'Stato & Condizione',
          tooltips: {
            constructionYear: {
              calculation: "Ricavata dall'anno di costruzione del registro federale degli edifici (REA)",
              description: "L'anno in cui l'edificio è stato originariamente completato",
              importance: "Data l'edificio ed è la base per età, stato e ristrutturazioni",
              limitations: "Per gli edifici più vecchi, può essere stimato dall'intervallo del periodo di costruzione. Le ricostruzioni importanti potrebbero non aggiornare questa data",
              source: 'REA',
              title: 'Anno di costruzione',
            },
            minergie: {
              calculation: "Assegnato dall'associazione Minergie sulla base del rispetto di rigorosi standard di efficienza energetica",
              description: 'Certificazione svizzera di efficienza energetica per gli edifici',
              importance: "Indica un'efficienza energetica superiore, maggiore comfort e costi operativi tipicamente inferiori",
              source: "Ufficio federale dell'energia UFE",
              title: 'Certificazione Minergie',
            },
            protected: {
              calculation: "Valutato dalle autorità cantonali del patrimonio in base all'importanza storica, architettonica o culturale",
              description: "Livello di protezione del patrimonio assegnato all'edificio dalle autorità cantonali",
              importance: 'Influisce sulle possibilità di ristrutturazione e sul potenziale di sviluppo',
              source: 'Ufficio cantonale del patrimonio',
              title: 'Stato di protezione',
            },
            renovation: {
              calculation: "Ricavata dall'anno dell'ultima ristrutturazione che ha aumentato il valore, secondo il registro degli edifici",
              description: "Anno dell'ultima ristrutturazione che ha aumentato sensibilmente il valore dell'edificio.",
              importance: "Indica se l'edificio è stato aggiornato dopo la costruzione",
              limitations: 'NON PIÙ AGGIORNATO NEL REA DAL 2017.',
              source: 'REA',
              title: 'Ultima ristrutturazione',
            },
            status: {
              calculation: 'Registrato nel registro degli edifici e aggiornato in base ai permessi di costruzione o demolizione',
              description: "Lo stato operativo attuale dell'edificio",
              importance: "Conferma se l'edificio esiste, è in costruzione o è stato demolito",
              source: 'REA',
              title: "Stato attuale dell'edificio",
            },
          },
        },
        statusCondition: {
          title: 'Stato e condizione',
        },
      },
      heightTypes: {
        highRise: 'Alto',
        lowRise: 'Basso',
        midRise: 'Medio',
      },
      labels: {
        asl: 'm s.l.m.',
        estimated: 'Stimato',
        period: 'Periodo',
      },
      metrics: {
        buildingAge: {
          label: 'Età',
          tooltip: {
            calculation: "Calcolato come anno corrente meno l'anno di costruzione dal registro ufficiale degli edifici",
            calculationEstimated: "Stimato in base al periodo di costruzione {{period}}. Il punto medio dell'intervallo viene utilizzato per il calcolo",
            description: "Il numero di anni dalla costruzione originale dell'edificio",
            importance: "Fattore chiave per valutare le esigenze di manutenzione, il potenziale di ristrutturazione, l'efficienza energetica e la valutazione immobiliare",
            limitations: "Non tiene conto delle ristrutturazioni importanti o ricostruzioni. Può essere stimato quando l'anno esatto dell'edificio è sconosciuto o un periodo (es: 1946-1960)",
            limitationsEstimated: "L'età è stimata dal periodo di costruzione e potrebbe non essere esatta. L'anno di costruzione effettivo può variare nell'intervallo del periodo",
            source: 'REA, Popety.io',
            title: "Età dell'edificio",
          },
        },
        buildingArea: {
          label: 'Superficie',
          tooltip: {
            calculation: "Misurato dai dati catastali ufficiali o dal registro degli edifici come proiezione orizzontale dell'involucro edilizio",
            description: "L'area a livello del suolo dell'edificio",
            importance: "Essenziale per comprendere l'efficienza d'uso del suolo, calcolare i rapporti di superficie e valutare il potenziale di sviluppo",
            limitations: 'Non include strutture esterne come terrazze, balconi o aree sotterranee. Può differire dalla superficie totale del piano',
            source: 'Popety.io, REA',
            title: "Superficie di base dell'edificio",
          },
        },
        buildingHeight: {
          label: 'Altezza',
          tooltip: {
            calculation: "Misurato da dati LiDAR o rilievi ufficiali, dal livello del suolo al colmo del tetto o all'elemento strutturale più alto",
            description: "La misura verticale dal livello del suolo al punto più alto dell'edificio",
            importance: 'Critico per la conformità urbanistica e la valutazione del potenziale di sviluppo o sopraelevazione',
            limitations: 'Può variare in base al metodo di misurazione. Antenne, camini e strutture temporanee possono essere inclusi o meno',
            source: 'Popety.io, swissSURFACE3D',
            title: "Altezza totale dell'edificio",
          },
        },
        floorCount: {
          label: 'Piani',
          tooltip: {
            calculation: "Conteggiato dai dati ufficiali del registro degli edifici, le mansarde e i seminterrati vengono conteggiati solo se sviluppati, anche parzialmente, per l'abitazione o riscaldati",
            description: "Il numero totale di piani e seminterrati dell'edificio, compreso il piano terra",
            importance: 'Indicatore chiave per la classificazione degli edifici, la conformità urbanistica e la stima della superficie lorda',
            limitations: 'Potrebbe non includere piani interrati, soppalchi o piani tecnici, le cantine non sono prese in considerazione.',
            source: 'REA',
            title: 'Numero totale di piani',
          },
        },
        numberOfFloors: {
          label: 'Numero di piani',
        },
      },
      sizeIndicators: {
        compact: 'Compatto',
        large: 'Grande',
        medium: 'Medio',
      },
      values: {
        asl: 's.l.m.',
        estimated: '(stim.)',
        estimatedFrom: 'Stimato da {{source}}',
        na: 'N/D',
        no: 'No',
        period: '(periodo)',
        years: 'anni',
        yearsAgo: '{{count}} anni fa',
        yearsCount: '{{count}} anni',
        yes: 'Sì',
      },
    },
    states: {
      error: 'Errore: {{error}}',
      loading: 'Caricamento...',
      noData: 'Nessun dato disponibile',
    },
    tabs: {
      neighborhood: 'Quartiere',
      overview: 'Panoramica',
      timeline: 'Cronologia',
    },
    timeline: {
      ageComparison: {
        comparisonTypes: {
          newerDescription: 'Questo edificio è stato costruito più recentemente della media del quartiere, probabilmente con standard più moderni',
          newerThanAverage: 'Più recente della media',
          olderDescription: "Questo edificio è significativamente più vecchio della media del quartiere, il che può influenzare le esigenze di ristrutturazione e l'efficienza energetica",
          significantlyOlder: 'Significativamente più vecchio',
          similarAge: 'Età simile',
          similarDescription: 'Questo edificio è stato costruito circa nello stesso periodo della maggior parte degli edifici del quartiere',
        },
        labels: {
          comparison: 'Confronto',
          neighborhoodAverage: 'Media del quartiere',
          thisBuilding: 'Questo edificio',
        },
        newerThanAverage: 'Più recente della media',
        significantlyOlder: 'Significativamente più vecchio',
        similarAge: 'Età simile',
        title: 'Confronto età',
        tooltip: {
          calculation: "Basato sull'anno di costruzione di questo edificio confrontato con l'anno medio di costruzione degli edifici vicini",
          description: "Confronta l'età di questo edificio con l'età media degli edifici nel quartiere circostante",
          importance: "Capire come l'età di un edificio si confronta con il suo quartiere aiuta a valutare le sue condizioni relative e il potenziale contesto di sviluppo",
          limitations: "Il confronto si basa sugli edifici nel raggio di analisi. Le aree con sviluppo misto possono mostrare un'alta varianza",
          source: 'REA, Popety.io',
          title: "Confronto d'età con il quartiere",
        },
        tooltips: {
          comparison: {
            description: "Come l'età di questo edificio si confronta con la media del quartiere",
            importance: 'Gli edifici più recenti possono avere una migliore efficienza energetica; quelli più vecchi possono avere valore di carattere o richiedere più manutenzione',
            source: 'Calcolato dai dati del registro degli edifici',
            title: "Risultato del confronto d'età",
          },
          neighborhoodAverage: {
            calculation: 'Anno medio di costruzione di tutti gli edifici nel raggio di analisi',
            description: "L'anno di costruzione e l'età media degli edifici nel quartiere circostante",
            importance: "Indica l'era costruttiva tipica del quartiere, fornendo contesto per l'edificio analizzato",
            source: 'REA, Popety.io',
            title: 'Media del quartiere',
          },
          thisBuilding: {
            calculation: 'Anno di costruzione dal registro ufficiale degli edifici; età calcolata come anno corrente meno anno di costruzione',
            description: "L'anno di costruzione e l'età dell'edificio analizzato",
            importance: "Punto di riferimento per il confronto d'età del quartiere",
            source: 'REA',
            title: 'Questo edificio',
          },
        },
        yearsOld: 'anni',
      },
      badges: {
        estimatedData: 'Dati stimati',
        historicalData: 'Dati storici',
      },
      cards: {
        ageAnalysis: {
          title: "Analisi dell'età",
          tooltips: {
            ageCategory: {
              description: "Classificazione dell'edificio in base alla sua età in categorie standardizzate",
              importance: "Colloca l'edificio in una classe di età usata per lo stato e il confronto di mercato",
              source: 'REA, Popety.io',
              title: "Categoria d'età",
            },
            buildingAge: {
              calculation: 'Calcolato come anno corrente meno anno di costruzione dal registro ufficiale degli edifici',
              description: "Il numero di anni dalla costruzione originale dell'edificio",
              importance: "Fattore chiave per valutare le esigenze di manutenzione, il potenziale di ristrutturazione, l'efficienza energetica e la valutazione immobiliare",
              limitations: "Non tiene conto delle ristrutturazioni importanti o ricostruzioni. Può essere stimato quando l'anno esatto dell'edificio è sconosciuto o un periodo (es: 1946-1960)",
              source: 'REA, Popety.io',
              title: "Età dell'edificio",
            },
            buildingPeriod: {
              calculation: 'Derivato dal registro degli edifici quando un anno di costruzione esatto non è disponibile',
              description: "L'era o il periodo di costruzione dell'edificio",
              importance: "Fornisce contesto sugli standard costruttivi e i materiali tipici di quell'epoca",
              title: 'Periodo di costruzione',
            },
            constructionPeriod: {
              calculation: "Ricavata dall'anno di costruzione e dalle fasce di periodo edilizio",
              source: 'REA',
            },
            constructionYear: {
              calculation: "Ricavata dall'anno di costruzione ufficiale nel registro degli edifici",
              importance: "Ancora la cronologia di costruzione e l'età dell'edificio",
              limitations: "Per gli edifici più vecchi, può essere stimato dall'intervallo del periodo di costruzione. Le ricostruzioni importanti potrebbero non aggiornare questa data",
              source: 'REA',
            },
          },
        },
        ageComparison: {
          title: "Confronto d'età con il quartiere",
        },
        constructionPhases: {
          title: 'Fasi di costruzione',
          tooltips: {
            constructionDates: {
              calculation: 'Estratte dai registri della storia costruttiva del registro degli edifici',
              description: 'Gli anni specifici in cui sono state completate le fasi di costruzione o ampliamento',
              importance: "Mostra quando sono avvenuti cambiamenti importanti, aiutando a comprendere l'evoluzione dell'edificio e gli standard costruttivi misti",
              source: 'REA',
              title: 'Date di costruzione',
            },
            currentStatus: {
              importance: "Conferma se l'edificio è operativo, in costruzione o demolito nel contesto della sua storia costruttiva",
              source: 'REA',
            },
            numberOfPhases: {
              calculation: 'Conteggio delle date di costruzione distinte registrate nel registro degli edifici',
              description: "Quante fasi distinte di costruzione o ampliamento ha subito l'edificio",
              importance: "Più fasi possono indicare estensioni o aggiunte che influenzano la struttura e il valore dell'edificio",
              source: 'REA',
              title: 'Numero di fasi di costruzione',
            },
            singlePhase: {
              calculation: 'Nessuna data di costruzione aggiuntiva trovata nel registro degli edifici',
              description: "L'edificio è stato costruito in una singola fase senza ampliamenti successivi",
              importance: 'Gli edifici a fase singola hanno tipicamente standard costruttivi più uniformi',
              source: 'REA',
            },
          },
        },
        constructionTimeline: {
          title: 'Cronologia costruzione',
        },
        renovationHistory: {
          title: 'Storia ristrutturazioni',
          tooltips: {
            lastRenovation: {
              calculation: "Ricavata dall'anno dell'ultima ristrutturazione che ha aumentato il valore nel registro degli edifici",
              description: "Anno dell'ultima ristrutturazione che ha aumentato sensibilmente il valore dell'edificio",
              importance: "Indica quanto è recente l'ultima ristrutturazione che ha aumentato il valore",
              limitations: 'NON PIÙ AGGIORNATO NEL REA DAL 2017.',
              source: 'REA',
            },
            period: {
              calculation: 'Derivato dai registri delle autorizzazioni di ristrutturazione nel registro degli edifici',
              description: "L'era o il periodo in cui la ristrutturazione è stata completata",
              importance: 'Fornisce contesto sul tipo e la qualità dei lavori di ristrutturazione eseguiti',
              title: 'Periodo ristrutturazione',
            },
            renovationPeriod: {
              calculation: "Ricavata dall'anno dell'ultima ristrutturazione e dalle fasce di periodo di ristrutturazione",
              importance: 'Colloca la ristrutturazione in un periodo usato per confrontare interventi della stessa epoca',
              limitations: 'NON PIÙ AGGIORNATO NEL REA DAL 2017.',
              source: 'REA',
            },
            renovationStatus: {
              limitations: 'NON PIÙ AGGIORNATO NEL REA DAL 2017.',
              source: 'Popety.io',
            },
            status: {
              description: "Valutazione delle esigenze di ristrutturazione dell'edificio basata sul tempo dall'ultima ristrutturazione",
              importance: 'Indicatore rapido per sapere se una ristrutturazione dovrebbe essere pianificata nel prossimo futuro',
              title: 'Stato ristrutturazione',
            },
            yearsSince: {
              calculation: "Anno corrente meno l'anno dell'ultima ristrutturazione dal registro degli edifici",
              description: "Il numero di anni trascorsi dall'ultima ristrutturazione che ha aumentato sensibilmente il valore dell'edificio",
              importance: 'Evidenzia gli edifici la cui ultima ristrutturazione valorizzante non è più recente',
              limitations: 'NON PIÙ AGGIORNATO NEL REA DAL 2017.',
              source: 'REA',
              title: 'Anni dalla ristrutturazione',
            },
          },
        },
        timeline: {
          title: 'Cronologia costruzione',
          tooltip: {
            calculation: 'Basato sulle date di costruzione ufficiali, i registri di ristrutturazione e i dati del registro degli edifici',
            description: "Rappresentazione visiva della storia costruttiva dell'edificio e degli eventi principali",
            importance: "Riassume le tappe di costruzione e ristrutturazione su un'unica cronologia",
            limitations: 'Alcune date possono essere stimate dai periodi di costruzione quando gli anni esatti non sono disponibili e alcuni eventi possono mancare',
            source: 'REA, Popety.io',
            title: 'Cronologia costruzione',
          },
        },
      },
      chart: {
        tooltipAge: 'Età',
        tooltipYear: 'Anno',
        xAxisLabel: 'Anno',
        yAxisLabel: 'Edificio',
      },
      events: {
        constructionPhase: 'Fase di costruzione',
        dwellingAdded: 'Abitazione aggiunta',
        majorRenovation: 'Ristrutturazione importante',
        originalConstruction: 'Costruzione originale',
        originalConstructionEst: 'Costruzione originale (Stim.)',
        present: 'Presente',
      },
      labels: {
        ageCategory: 'Categoria età',
        buildingAge: 'Età edificio',
        buildingPeriod: 'Periodo di costruzione',
        comparison: 'Confronto',
        constructionDates: 'Date di costruzione',
        constructionYear: 'Anno di costruzione',
        lastRenovation: 'Ultima ristrutturazione',
        neighborhoodAverage: 'Media del quartiere',
        noRecord: 'Nessun record',
        numberOfPhases: 'Numero di fasi',
        phases: 'fasi',
        renovationPeriod: 'Periodo ristrutturazione',
        renovationStatus: 'Stato ristrutturazione',
        singlePhaseConstruction: 'Costruzione monofase',
        thisBuilding: 'Questo edificio',
        yearsSinceRenovation: 'Anni dalla ristrutturazione',
      },
      noData: 'Nessun dato cronologico disponibile',
      noDataMessage: 'Nessun dato sulla cronologia di costruzione disponibile',
      phaseLabels: {
        noRecord: 'Nessun record',
        singlePhase: 'Costruzione a fase unica',
      },
      renovationCategories: {
        dueSoon: {
          description: "L'edificio potrebbe beneficiare di ristrutturazione o modernizzazione nel prossimo futuro",
          label: 'Da fare presto',
          range: '15-30 anni',
        },
        good: {
          description: "La ristrutturazione è relativamente recente; l'edificio dovrebbe essere ancora in buone condizioni",
          label: 'Buone condizioni',
          range: '5-15 anni',
        },
        overdue: {
          description: "È trascorso un tempo significativo dall'ultima ristrutturazione; sono probabilmente necessari lavori importanti",
          label: 'In ritardo',
          range: '> 30 anni',
        },
        recentlyRenovated: {
          description: "L'edificio è stato ristrutturato negli ultimi 5 anni ed è probabilmente in ottime condizioni",
          label: 'Recentemente ristrutturato',
          range: '< 5 anni',
        },
      },
      renovationStatuses: {
        considerUpdates: 'Considerare aggiornamenti',
        goodCondition: 'Buone condizioni',
        noRenovation: 'Nessuna ristrutturazione',
        recentlyRenovated: 'Recentemente ristrutturato',
        renovationNeeded: 'Ristrutturazione necessaria',
      },
    },
  },
  gwr: {
    buildingCategory: {
      "Bâtiment à usage partiel d'habitation": 'Edificio ad uso parzialmente abitativo',
      "Bâtiment d'habitation avec usage annexe": 'Edificio abitativo con utilizzazione accessoria',
      "Bâtiment exclusivement à usage d'habitation": 'Edificio ad uso esclusivamente abitativo',
      "Bâtiment partiellement à usage d'habitation": 'Edificio ad uso parzialmente abitativo',
      "Bâtiment sans usage d'habitation": 'Edificio senza uso abitativo',
      'Construction particulière': 'Costruzione speciale',
      'Habitation provisoire': 'Abitazione provvisoria',
    },
    buildingClass: {
      'Autre bâtiment non classé ailleurs': 'Altro edificio non classificato altrove',
      "Autres bâtiments d'exploitation agricole": 'Altri edifici agricoli',
      "Autres bâtiments d'hébergement de tourisme": 'Altri edifici per abitazione di breve durata',
      'Autres bâtiments non classés ailleurs': 'Altri edifici non classificati altrove',
      "Autres bâtiments pour l'hébergement collectif": 'Altri edifici per abitazioni collettive',
      "Autres bâtiments pour l'hébergement de tourisme": 'Altri edifici per abitazione di breve durata',
      "Bâtiment à usage d'habitation non déterminé": 'Edificio abitativo, non meglio specificato',
      'Bâtiment à usage mixte': 'Edificio ad uso misto',
      'Bâtiment administratif': 'Edificio amministrativo',
      'Bâtiment commercial': 'Edificio commerciale',
      "Bâtiment d'exploitation agricole": 'Edificio rurale',
      'Bâtiment de culte': 'Edificio di culto',
      'Bâtiment industriel': 'Edificio industriale',
      'Bâtiment pour les transports et les communications': 'Edificio per trasporti e comunicazioni',
      'Bâtiment scolaire': 'Edificio scolastico',
      'Bâtiments administratifs': 'Edifici amministrativi',
      'Bâtiments commerciaux': 'Edifici commerciali',
      "Bâtiments d'exploitation agricole": 'Edifici rurali',
      'Bâtiments de culte': 'Edifici di culto',
      'Bâtiments industriels': 'Edifici industriali',
      "Bâtiments pour la garde d'animaux": 'Edifici per la custodia di animali',
      'Bâtiments pour les cultures végétales': 'Edifici per le colture vegetali',
      'Bâtiments scolaires': 'Edifici scolastici',
      Garage: 'Garage',
      Garages: 'Garage',
      'Gares, aérogares, centraux téléphoniques': 'Stazioni, terminal, centrali telefoniche',
      'Habitat communautaire': 'Residenze per collettività',
      'Hôpital et établissement de santé': 'Ospedale e struttura sanitaria',
      'Hôpitaux et établissements de santé': 'Ospedali e strutture sanitarie',
      Hôtel: 'Albergo',
      Hôtels: 'Alberghi',
      'Immeuble à trois logements et plus': 'Edificio a tre e più abitazioni',
      'Maison à deux logements': 'Edificio a due abitazioni',
      'Maison individuelle': 'Casa unifamiliare',
      'Monument historique ou classé': 'Monumento storico o classificato',
      'Monuments historiques ou classés': 'Monumenti storici o classificati',
      'Réservoir, silo et entrepôt': 'Serbatoi, silos e magazzini',
      'Réservoirs, silos et entrepôts': 'Serbatoi, silos e magazzini',
      'Salle de sport': 'Sala sportiva',
      'Salle de tir': 'Sala di tiro',
      'Salles de sport': 'Sale sportive',
      'Salles de tir': 'Sale di tiro',
    },
    buildingStatus: {
      autorisé: 'autorizzato',
      démoli: 'demolito',
      'en construction': 'in costruzione',
      existant: 'esistente',
      inutilisable: 'inutilizzabile',
      'non réalisé': 'non realizzato',
      projeté: 'in progetto',
    },
    energySource: {
      Air: 'Aria',
      Aucun: 'Nessuno',
      Aucune: 'No',
      Autre: 'Altro',
      'Bois (bois déchiqueté, copeaux)': 'Legno (legno triturato, trucioli)',
      'Bois (bûches)': 'Legno (tronchi)',
      'Bois (générique)': 'Legno (generico)',
      'Bois (granulés - pellets)': 'Legno (pellet)',
      'Capteurs solaires thermiques': 'Collettori solari termici',
      'Chaleur produite à distance (basse température)': 'Produzione di calore a distanza (bassa temperatura)',
      'Chaleur produite à distance (générique)': 'Generazione di calore a distanza (generico)',
      'Chaleur produite à distance (haute température)': 'Produzione di calore a distanza (alta temperatura)',
      'Eau (nappe phréatique, eaux superficielles, eaux résiduelles)': 'Acqua (acque sotterranee, acque superficiali, acque reflue)',
      Electricité: 'Elettricità',
      Gaz: 'Gas',
      Indéterminé: 'Indeterminato',
      Mazout: 'Olio combustibile',
      'Pas de générateur': 'Nessun generatore',
      'Pompe à chaleur': 'Pompa di calore',
      'Rejets thermiques (dans le bâtiment)': "Scarichi termici (nell'edificio)",
      'Serpentin géothermique': 'Bobina geotermica',
      'Soleil (thermique)': 'Sole (termico)',
      'Sonde géothermique': 'Sonda geotermica',
    },
    heaterGenerator: {
      Autre: 'Altro',
      'Boiler électrique central': 'Boiler elettrico centrale',
      'Chaudière (générique)': 'Caldaia (generica)',
      'Chaudière (générique) pour plusieurs bâtiments': 'Caldaia (generica) per più edifici',
      'Chaudière (générique) pour un seul bâtiment': 'Caldaia (generica) per un singolo edificio',
      'Chaudière à condensation': 'Caldaia a condensazione',
      'Chaudière à condensation pour plusieurs bâtiments': 'Caldaia a condensazione per più edifici',
      'Chaudière à condensation pour un seul bâtiment': 'Caldaia a condensazione per un singolo edificio',
      'Chaudière standard': 'Caldaia standard',
      'Chaudière standard pour plusieurs bâtiments': 'Caldaia standard per più edifici',
      'Chaudière standard pour un seul bâtiment': 'Caldaia standard per un singolo edificio',
      'Chauffage central électrique': 'Riscaldamento centrale elettrico',
      'Chauffage central électrique pour plusieurs bâtiments': 'Riscaldamento centrale elettrico per più edifici',
      'Chauffage central électrique pour un seul bâtiment': 'Riscaldamento centrale elettrico per un singolo edificio',
      'Chauffage électrique direct (y compris radiateur infra-rouge)': 'Riscaldamento elettrico diretto (incl. infrarossi)',
      'Échangeur de chaleur (y compris pour la chaleur à distance)': 'Scambiatore di calore (incluso teleriscaldamento)',
      'Echangeur de chaleur (y.c. chauffage à distance)': 'Scambiatore di calore (incl. teleriscaldamento)',
      'Echangeur de chaleur (y.c. chauffage à distance) pour plusieurs bâtiments': 'Scambiatore di calore per più edifici',
      'Echangeur de chaleur (y.c. chauffage à distance) pour un seul bâtiment': 'Scambiatore di calore per un singolo edificio',
      Indéterminé: 'Indeterminato',
      'Installation couplage chaleur-force': 'Impianto di cogenerazione',
      'Installation couplage chaleur-force pour plusieurs bâtiments': 'Cogenerazione per più edifici',
      'Installation couplage chaleur-force pour un seul bâtiment': 'Cogenerazione per un singolo edificio',
      'Installation solaire thermique': 'Impianto solare termico',
      'Installation solaire thermique pour plusieurs bâtiments': 'Solare termico per più edifici',
      'Installation solaire thermique pour un seul bâtiment': 'Solare termico per un singolo edificio',
      'Pas de générateur de chaleur': 'Nessun generatore di calore',
      Poêle: 'Stufa',
      'Pompe à chaleur PAC': 'Pompa di calore',
      'Pompe à chaleur PAC pour plusieurs bâtiments': 'Pompa di calore per più edifici',
      'Pompe à chaleur PAC pour un seul bâtiment': 'Pompa di calore per un singolo edificio',
    },
  },
  ui: {
    exportPdf: {
      unavailable: 'Questa esportazione PDF non è disponibile. Chiudi l’anteprima e riprova.',
    },
    aiFilter: {
      analyzing: 'Analisi della vostra richiesta...',
      apply: 'Applica',
      clearHistory: 'Cancella',
      description: 'Descrivete ciò che cercate in linguaggio naturale e i filtri appropriati verranno applicati.',
      errors: {
        microphoneAccess: 'Impossibile accedere al microfono. Verificate le vostre autorizzazioni.',
        textProcessing: "Si è verificato un errore durante l'elaborazione della vostra richiesta",
        voiceNotConfigured: "L'input vocale non è configurato per questo filtro.",
        voiceProcessing: "Si è verificato un errore durante l'elaborazione della vostra richiesta vocale",
      },
      examplePrompts: {
        buildings: 'Immobili costruiti dopo il 2020 con parcheggio',
        properties: 'Mostratemi gli immobili oltre 100m² in centro città',
        transactions: 'Trovate le transazioni recenti superiori a 1 milione',
      },
      filtersApplied: 'Filtri applicati',
      filterSaved: 'Filtro salvato',
      placeholder: 'Digitate o dettate la vostra richiesta di filtro...',
      recentSearches: 'Ricerche recenti',
      recording: 'Registrazione...',
      saveAsFilter: 'Salva come filtro',
      startRecording: 'Avvia registrazione vocale',
      stopRecording: 'Interrompi registrazione',
      title: 'Assistente filtro IA',
      trySaying: 'Esempi:',
    },
    analysisMap: {
      baseLayers: {
        building: 'Edificio',
        building3d: 'Edificio 3D',
        buildingNumbers: 'Numeri edificio',
        chooseMapStyle: 'Scegli lo stile della mappa',
        contours: 'Curve di livello',
        dimensionMetrics: 'Metriche dimensionali',
        distanceLimit: 'Limite di distanza',
        footprint: 'Impronta',
        labels: 'Etichette',
        land: 'Terreno',
        mapBase: 'Base mappa',
        mapFeatures: 'Elementi della mappa',
        parcelBoundaries: 'Confini parcellari',
        pointCloud: 'Nuvola di punti',
        streets: 'Strade',
        terrain: 'Terreno',
      },
      controls: {
        hideMap: 'Nascondi la mappa',
      },
      groupLabels: {
        demographicsLayer: 'Livello demografico',
        employmentLayer: 'Livello occupazionale',
        historicalImagery: 'Immagini storiche',
        noiseLayer: 'Livello rumore',
        purchaseMarketLayer: "Livello mercato d'acquisto",
        rentalMarketLayer: 'Livello mercato locativo',
        zoningLayer: 'Livello zonizzazione',
      },
      groupTitles: {
        amenities: 'Servizi',
        buildingPermits: 'Permessi di costruzione',
        demographics: 'Demografia',
        dynamicLayers: 'Livelli dinamici',
        employment: 'Occupazione',
        energy: 'Energia',
        historicalSatelliteImages: 'Immagini satellitari storiche',
        layers: 'Livelli',
        naturalHazards: 'Pericoli naturali',
        noiseAnalysis: 'Analisi del rumore',
        purchaseListings: 'Annunci di vendita',
        purchaseMarket: "Mercato d'acquisto",
        rentalListings: 'Annunci di locazione',
        rentalMarket: 'Mercato locativo',
        restrictions: 'Restrizioni',
        sunlightStudy: "Studio dell'irraggiamento solare",
        transactions: 'Transazioni',
        transportAccess: 'Trasporti & Accesso',
        zoning: 'Zonizzazione',
        zoningRules: 'Regole di zonizzazione',
      },
      hazardTypes: {
        avalanche: 'Valanga',
        flood: 'Alluvione',
        landslide: 'Frana',
        rockslide: 'Caduta massi',
        title: 'Tipi di pericolo',
      },
      layers: {
        amenitiesCulture: 'Cultura',
        amenitiesCultureDescription: 'Musei, teatri, luoghi culturali',
        amenitiesDining: 'Ristorazione',
        amenitiesDiningDescription: 'Ristoranti, caffè, bar',
        amenitiesEducation: 'Istruzione',
        amenitiesEducationDescription: 'Scuole, università, biblioteche',
        amenitiesGrocery: 'Alimentari',
        amenitiesGroceryDescription: 'Supermercati, negozi di alimentari',
        amenitiesHealthcare: 'Sanità',
        amenitiesHealthcareDescription: 'Ospedali, cliniche, farmacie',
        amenitiesServices: 'Servizi',
        amenitiesServicesDescription: 'Banche, uffici postali, servizi pubblici',
        amenitiesShopping: 'Shopping',
        amenitiesShoppingDescription: 'Centri commerciali, gallerie, negozi al dettaglio',
        amenitiesTransport: 'Trasporti',
        amenitiesTransportDescription: 'Fermate e stazioni dei trasporti pubblici',
        amenitiesWellness: 'Benessere',
        amenitiesWellnessDescription: 'Palestre, spa, centri benessere',
        buildingPermitsMarkers: 'Permessi di costruzione',
        buildingPermitsMarkersDescription: 'Visualizza le posizioni dei permessi di costruzione sulla mappa',
        cyclingTravelTime: 'Tempo di percorrenza in bicicletta',
        cyclingTravelTimeDescription: 'Zone ciclabili di 5, 10, 15, 30 minuti',
        demographicsAgeGroup: "Gruppo d'età dominante",
        demographicsAgeGroupDescription: 'Segmento demografico dominante per il marketing mirato',
        demographicsElderlyPct: 'Anziani (65+)',
        demographicsElderlyPctDescription: 'Domanda di alloggi accessibili e prossimità alle cure sanitarie',
        demographicsForeignPct: 'Popolazione straniera',
        demographicsForeignPctDescription: 'Indicatore della domanda di mercato internazionale',
        demographicsGrowthRate: 'Crescita demografica (5 anni)',
        demographicsGrowthRateDescription: 'Slancio del mercato e potenziale di investimento',
        demographicsHouseholdType: 'Tipo di nucleo familiare dominante',
        demographicsHouseholdTypeDescription: 'Domanda di dimensioni abitative (monolocali vs. case familiari)',
        demographicsIsochrone: 'Area di analisi',
        demographicsIsochroneDescription: "Mostra l'area di tempo di percorrenza utilizzata per l'analisi demografica",
        demographicsPopulationDensity: 'Densità della popolazione',
        demographicsPopulationDensityDescription: 'Indicatore centrale di urbanizzazione per la domanda immobiliare',
        demographicsResidenceDuration: 'Durata di residenza dominante',
        demographicsResidenceDurationDescription: 'Indicatore di stabilità del quartiere e turnover degli inquilini',
        demographicsYoungAdultPct: 'Giovani adulti (20-39 anni)',
        demographicsYoungAdultPctDescription: 'Forza del mercato locativo e domanda di prime abitazioni',
        drivingTravelTime: 'Tempo di percorrenza in auto',
        drivingTravelTimeDescription: 'Zone di guida di 5, 10, 15, 30 minuti',
        employmentDensity: 'Densità occupazionale',
        employmentDensityDescription: "Indicatore della domanda immobiliare commerciale e dell'attività economica",
        employmentDominantSector: 'Settore occupazionale dominante',
        employmentDominantSectorDescription: 'Carattere economico (Primario/Secondario/Terziario)',
        employmentFulltimeRatio: 'Quota di occupazione a tempo pieno',
        employmentFulltimeRatioDescription: 'Stabilità del reddito e solidità della domanda residenziale',
        employmentGrowthRate: "Crescita dell'occupazione (5 anni)",
        employmentGrowthRateDescription: 'Slancio del mercato e potenziale di investimento',
        employmentIsochrone: 'Area di analisi',
        employmentIsochroneDescription: "Mostra l'area di tempo di percorrenza utilizzata per l'analisi occupazionale",
        employmentJobsPerEstablishment: 'Posti di lavoro per stabilimento',
        employmentJobsPerEstablishmentDescription: 'Indicatore della dimensione aziendale (grandi imprese vs. PMI)',
        employmentPublicPrivate: 'Settore pubblico vs. privato',
        employmentPublicPrivateDescription: 'Presenza pubblica e stabilità degli inquilini',
        listingsMarkers: 'Marcatori annunci',
        listingsMarkersDescription: 'Visualizza le singole inserzioni immobiliari sulla mappa',
        nearbyBuildings: 'Edifici vicini',
        noiseRailwayDay: 'Rumore ferroviario - Giorno',
        noiseRailwayDayDescription: 'Livelli di rumore ferroviario diurni in dB',
        noiseRailwayNight: 'Rumore ferroviario - Notte',
        noiseRailwayNightDescription: 'Livelli di rumore ferroviario notturni in dB',
        noiseRoadDay: 'Rumore stradale - Giorno',
        noiseRoadDayDescription: 'Livelli di rumore stradale diurni in dB',
        noiseRoadNight: 'Rumore stradale - Notte',
        noiseRoadNightDescription: 'Livelli di rumore stradale notturni in dB',
        purchaseMarketApartmentPrice: "Prezzo d'acquisto medio/m² - Appartamento",
        purchaseMarketApartmentPriceDescription: "Prezzo d'acquisto medio per metro quadrato per appartamenti nell'area",
        purchaseMarketHousePrice: "Prezzo d'acquisto medio/m² - Casa",
        purchaseMarketHousePriceDescription: "Prezzo d'acquisto medio per metro quadrato per case nell'area",
        rentalMarketApartmentPrice: 'Canone medio/m² - Appartamento',
        rentalMarketApartmentPriceDescription: "Canone medio per metro quadrato per appartamenti nell'area",
        rentalMarketHousePrice: 'Canone medio/m² - Casa',
        rentalMarketHousePriceDescription: "Canone medio per metro quadrato per case nell'area",
        restrictionsBase: 'Restrizioni',
        restrictionsBaseDescription: "Restrizioni e vincoli sull'uso del suolo",
        shadowSimulation: 'Simulazione ombre',
        shadowSimulationDescription: 'Simulazione ombre in tempo reale basata su data e ora',
        solarPotential: 'Potenziale solare',
        solarPotentialDescription: 'Sezioni del tetto colorate per potenziale solare (kWh/m²/anno)',
        sunDirection: 'Direzione del sole',
        sunDirectionDescription: "Mostra la direzione dell'alba, del tramonto e del sole attuale come linee dal centro della mappa",
        sunlightHours: 'Ore di sole',
        sunlightHoursDescription: "Visualizzazione a colori che mostra le ore totali di esposizione solare giornaliera dall'alba al tramonto",
        transactionPoints: 'Punti di transazione',
        transactionPointsDescription: 'Visualizza le transazioni immobiliari storiche',
        transportBusStops: 'Fermate bus',
        transportBusStopsDescription: 'Fermate bus pubbliche nel raggio di 1.5 km',
        transportEvCharging: 'Stazioni di ricarica VE',
        transportEvChargingDescription: 'Stazioni di ricarica per veicoli elettrici nel raggio di 5 km',
        transportHighwayAccess: 'Accesso autostradale',
        transportHighwayAccessDescription: 'Uscite e ingressi autostradali nel raggio di 15 km',
        transportTrainStops: 'Stazioni ferroviarie',
        transportTrainStopsDescription: 'Stazioni ferroviarie nel raggio di 1.5 km',
        transportTramStops: 'Fermate tram',
        transportTramStopsDescription: 'Fermate tram nel raggio di 1.5 km',
        walkingTravelTime: 'Tempo di percorrenza a piedi',
        walkingTravelTimeDescription: 'Zone pedonali di 5, 10, 15, 30 minuti',
        zoningAll: 'Tutti i tipi di zona',
        zoningAllDescription: 'Visualizza tutti i tipi di zona (Generale, Cantonale, Locale, Parziale)',
        zoningCantonalOnly: 'Solo cantonale',
        zoningCantonalOnlyDescription: 'Visualizza solo le zone di zonizzazione cantonale',
        zoningGeneralOnly: 'Solo generale',
        zoningGeneralOnlyDescription: 'Visualizza solo le zone di zonizzazione generale',
        zoningLocalOnly: 'Solo locale',
        zoningLocalOnlyDescription: 'Visualizza solo le zone di zonizzazione locale',
        zoningMapStyle: 'Mappa di zonizzazione',
        zoningMapStyleDescription: 'Visualizza la zonizzazione sulla mappa standard',
        zoningPartialOnly: 'Solo parziale',
        zoningPartialOnlyDescription: 'Visualizza solo le zone di zonizzazione parziale',
        zoningSatelliteMapStyle: 'Mappa di zonizzazione satellite',
        zoningSatelliteMapStyleDescription: "Visualizza la zonizzazione sull'immagine satellitare",
      },
      layersPanel: {
        closePanel: 'Chiudi il pannello livelli',
        disabledSelectLayer: 'Disattivato - Seleziona un livello',
        disableLayer: 'Disattiva il livello',
        hideAll: 'Nascondi tutto',
        opacity: 'Opacità',
        searchLayers: 'Cerca livelli',
        searchPlaceholder: 'Cerca livelli...',
        selectLayer: 'Seleziona...',
        showAll: 'Mostra tutto',
        title: 'Livelli mappa',
      },
      legends: {
        cyclingTravelTime: 'Tempo di percorrenza in bicicletta',
        drivingTravelTime: 'Tempo di percorrenza in auto',
        min: '{{count}} min',
        walkingTravelTime: 'Tempo di percorrenza a piedi',
      },
      mapStyles: {
        light: 'Chiaro',
        lightDescription: 'Stile minimale per la visualizzazione dati',
        outdoors: 'Outdoor',
        outdoorsDescription: 'Mappa outdoor con terreno e sentieri escursionistici',
        popetyio: 'Popety.io',
        popetyioDescription: 'Stile mappa Popety.io per la Svizzera',
        satellite: 'Satellite',
        satelliteDescription: 'Immagini aeree con sovrapposizione stradale',
        standard: 'Standard',
        standardDescription: 'Stile mappa stradale con edifici 3D',
        traffic: 'Traffico',
        trafficDescription: 'Mappa con condizioni di traffico',
        zoning: 'Zonizzazione',
        zoningDescription: 'Stile personalizzato con colori di zonizzazione',
        zoningSatellite: 'Zonizzazione Satellite',
        zoningSatelliteDescription: 'Immagini satellitari con sovrapposizione di zonizzazione',
      },
      restrictionTypes: 'Tipi di restrizioni',
      sunDirectionLegend: {
        currentSun: 'Sole attuale',
        helpText: 'Le linee si irradiano dal centro della mappa mostrando la direzione del sole',
        sunrise: 'Alba',
        sunset: 'Tramonto',
        title: 'Direzione del sole',
      },
      sunlightControls: {
        date: 'Data',
        now: 'Adesso',
        spring: 'Primavera',
        summer: 'Estate',
        sunExposureInfo: "Esposizione solare giornaliera dall'alba al tramonto",
        time: 'Ora',
        timeline: 'Cronologia del giorno',
        today: 'Oggi',
        winter: 'Inverno',
      },
    },
    common: {
      actions: {
        back: 'Indietro',
        cancel: 'Annulla',
        close: 'Chiudi',
        confirm: 'Conferma',
        delete: 'Elimina',
        edit: 'Modifica',
        loadMore: 'Carica altro',
        next: 'Avanti',
        save: 'Salva',
        submit: 'Invia',
        viewDetails: 'Visualizza dettagli',
      },
      no: 'No',
      status: {
        error: 'Si è verificato un errore',
        loading: 'Caricamento...',
        saving: 'Salvataggio...',
        success: 'Successo',
      },
      validation: {
        invalidEmail: 'Indirizzo email non valido',
        invalidFormat: 'Formato non valido',
        maxLength: 'Massimo {{count}} caratteri',
        minLength: 'Minimo {{count}} caratteri',
        required: 'Questo campo è obbligatorio',
      },
      yes: 'Sì',
    },
    dataTable: {
      actions: 'Azioni',
      bulkActions: {
        exportAsExcel: 'Esporta come Excel',
        itemsSelected: '{{count}} elementi selezionati',
      },
      columns: {
        button: 'Colonne',
        dragHint: 'Trascina le intestazioni per riordinare le colonne',
        moveColumn: 'Sposta la colonna {{column}}',
        resetToDefault: 'Ripristina predefiniti',
        showHide: 'Mostra/Nascondi colonne',
      },
      empty: {
        description: 'Prova a modificare i filtri o i criteri di ricerca',
        title: 'Nessun dato trovato',
      },
      error: {
        description: 'Si è verificato un errore durante il caricamento dei dati',
        title: 'Errore nel caricamento dei dati',
      },
      loading: 'Caricamento dati...',
      mobile: {
        pullToRefresh: 'Trascina per aggiornare',
        refreshing: 'Aggiornamento...',
        releaseToRefresh: 'Rilascia per aggiornare',
        selectColumn: 'Seleziona colonna',
        showLess: 'Mostra meno',
        showMore: 'Mostra di più',
        sortAscending: 'Ordine crescente',
        sortBy: 'Ordina per',
        sortDescending: 'Ordine decrescente',
      },
      pagination: {
        items: 'elementi',
        of: 'di',
        show: 'Mostra',
        showing: 'Visualizzazione di',
      },
    },
    detailPanel: {
      close: 'Chiudi',
      closePanel: 'Chiudi pannello',
      viewDetails: 'Visualizza dettagli',
    },
    drawingTools: {
      '3dMode': 'Modalità 3D',
      deleteAll: 'Elimina tutto',
      deleteSelected: 'Elimina selezionati ({{count}})',
      drawLine: 'Disegna linea',
      drawPolygon: 'Disegna poligono',
      measureDistance: 'Misura distanza',
      print: 'Stampa la mappa',
      tooltip: 'Strumenti di disegno',
    },
    files: {
      categories: {
        cadFiles: 'File CAD',
        documents: 'Documenti',
        images: 'Immagini',
        other: 'Altri',
        pdfDocuments: 'Documenti PDF',
        spreadsheets: 'Fogli di calcolo',
      },
      confirmDelete: 'Eliminare questo file?',
      deleteFile: 'Elimina file',
      downloadFile: 'Scarica file',
      emptyDescription: 'Carica file per organizzare i documenti importanti',
      emptyTitle: 'Nessun file caricato',
      retry: 'Riprova',
      searchPlaceholder: 'Cerca file...',
      searchResults: '{{count}} risultato su {{total}} elementi',
      searchResultsPlural: '{{count}} risultati su {{total}} elementi',
      time: {
        daysAgo: '{{count}}g fa',
        hoursAgo: '{{count}}h fa',
        justNow: 'Proprio ora',
        lastWeek: 'La settimana scorsa',
        minutesAgo: '{{count}} min fa',
        weeksAgo: '{{count}} settimane fa',
        yesterday: 'Ieri',
      },
      title: 'File',
      uploadFile: 'Carica file',
      uploadHint: 'PDF, DOC, XLS, DWG, Immagini (max {{maxSize}}MB)',
      uploadText: 'Clicca per caricare o trascina i file qui',
    },
    filters: {
      filterContainer: {
        clearAll: 'Cancella tutto',
        hideFilters: 'Nascondi filtri',
        lessFilters: 'Meno filtri',
        loading: 'Caricamento filtri...',
        moreFilters: 'Altri filtri',
        resultsFound: '{{count}} risultati trovati',
        showFilters: 'Mostra filtri',
      },
      filterPanel: {
        apply: 'Applica',
        clear: 'Cancella',
        emptyState: 'Le opzioni di filtro verranno visualizzate qui',
        filters: 'Filtri',
        invalidRange: 'Il minimo deve essere inferiore al massimo — i risultati non vengono aggiornati',
        reset: 'Ripristina',
        resetFilters: 'Ripristina filtri',
        toggleFilters: 'Attiva/disattiva filtri',
      },
      quickFilters: {
        activeCount_zero: 'Nessun filtro attivo',
        activeCount_one: '{{count}} filtro attivo',
        activeCount_other: '{{count}} filtri attivi',
        activeFilters: 'Filtri attivi',
        removeFilter: 'Rimuovi filtro {{label}}',
        reset: 'Ripristina',
        save: 'Salva filtro',
        viewLess: 'Mostra meno',
        viewMore: '+{{count}} altri',
      },
    },
    forgotPassword: {
      backToLogin: 'Torna al login',
      description: 'Inserisci il tuo indirizzo email e ti invieremo un link per reimpostare la password.',
      emailLabel: 'Indirizzo email',
      emailPlaceholder: 'Inserisci la tua email',
      emailRequired: "L'email è obbligatoria",
      errorMessage: 'Si è verificato un errore. Riprova.',
      invalidEmail: 'Inserisci un indirizzo email valido',
      sending: 'Invio in corso...',
      sendLink: 'Invia link',
      submitButton: 'Invia nuova password',
      success: 'Controlla la tua email per un link per reimpostare la password.',
      successMessage: "Se l'email esiste, una nuova password è stata inviata al tuo indirizzo.",
      title: 'Reimposta password',
    },
    locationFilter: {
      clear: 'Cancella posizione',
      noResults: 'Nessuna posizione trovata',
      placeholder: 'Cerca posizione...',
      types: {
        address: 'Indirizzo',
        district: 'Distretto',
        municipality: 'Comune',
        postalCode: 'CAP',
        region: 'Regione',
      },
    },
    login: {
      continueWithGoogle: 'Continua con Google',
      email: 'Email',
      emailPlaceholder: 'Inserisci la tua email',
      forgotPassword: 'Password dimenticata?',
      noAccount: 'Non hai un account?',
      orContinueWith: 'Oppure continua con',
      password: 'Password',
      passwordPlaceholder: 'Inserisci la tua password',
      rememberMe: 'Ricordami',
      signIn: 'Accedi',
      signingIn: 'Accesso in corso...',
      signUp: 'Registrati',
      title: 'Accedi',
    },
    map: {
      controls: {
        flyToLocation: 'Vai alla mia posizione',
        flyToProperty: "Vai all'immobile",
        fullscreen: 'Schermo intero',
        myLocation: 'La mia posizione',
        resetNorth: 'Orienta verso nord',
        resetView: 'Ripristina vista',
        showMap: 'Mostra la mappa',
        showSatellite: 'Mostra il satellite',
        zoomIn: 'Ingrandisci',
        zoomOut: 'Riduci',
      },
      layers: {
        map: 'Mappa',
        satellite: 'Satellite',
        streets: 'Strade',
        terrain: 'Terreno',
      },
      vectorTile: {
        error: 'Errore nel caricamento dei dati della mappa: {{error}}',
        featuresAtLocation: '{{count}} elementi in questa posizione',
        listingsAtLocation: '{{count}} annunci in questa posizione',
        loading: 'Caricamento dati della mappa...',
        overlapCount: '{{count}} altri. Clicca per vedere tutti',
        scrollFeatures: 'Scorri per vedere tutti i {{count}} elementi',
        scrollListings: 'Scorri per vedere tutti i {{count}} annunci',
      },
    },
    metricSourceModal: {
      agreement: {
        conflict: 'Conflitto — 2 fonti non concordano su questo valore',
        userModified: "Modificato dall'utente — Valore originale da {{source}}: {{value}}",
      },
      badges: {
        estimate: 'STIMA',
        geoportal: 'GEO',
        llm: 'REG',
        manual: 'EDIT',
        old: 'VEC',
      },
      boundSelector: {
        ariaLabel: 'Seleziona min o max',
        max: 'MAX',
        min: 'MIN',
      },
      estimate: {
        dotStripLabel: 'Distribuzione di {{metric}} su {{count}} particelle — punti più grandi = più particelle',
        dotTooltip: '{{count}} particella/e in questo intervallo',
        legendEstimate: 'Valore stimato ({{value}})',
        legendMedian: 'Mediana ({{value}})',
        legendParcel: '1 particella',
        legendParcelScale: 'Meno / più particelle',
        loading: 'Caricamento delle statistiche...',
        noBuildings: 'Nessun edificio trovato in questa zona per calcolare le statistiche',
        rangeText: 'I valori in questa zona vanno da {{min}} a {{max}} (mediana: {{median}})',
        step1Desc: '{{buildingCount}} edifici su {{landCount}} particelle sono stati utilizzati per il calcolo',
        step1Title: 'Abbiamo analizzato gli edifici esistenti in questa zona',
        step2Desc: {
          cos: "Superficie dell'impronta dell'edificio divisa per la superficie del terreno",
          cus: 'Superficie totale dei piani divisa per la superficie del terreno',
          floor_nb: 'Numero massimo di piani per edificio su ogni particella',
          height: "Altezza massima dell'edificio su ogni particella",
          ibus: "Superficie lorda dell'edificio divisa per la superficie del terreno",
          im: "Massa dell'edificio (volume) rispetto alla superficie del terreno",
          unit_nb: 'Numero massimo di unità per edificio su ogni particella',
          vbr: "Volume dell'edificio diviso per la superficie del terreno",
        },
        step2Title: '{{metric}} è stato calcolato per ogni particella',
        step3Desc: 'Il {{percentile}}% delle particelle ha un {{metric}} pari o inferiore a {{value}}',
        step3Title: 'Il valore riflette ciò che la maggior parte delle particelle consente',
        title: 'Come è stato calcolato questo valore?',
      },
      geoportal: {
        openTab: 'Apri in una nuova scheda',
        title: 'Geoportale cantonale',
        viewLink: 'Visualizza sul geoportale cantonale',
      },
      labels: {
        confirmedValue: 'Valore confermato',
        displayedValue: 'Valore visualizzato',
        maximumValue: 'Valore massimo',
        minimumValue: 'Valore minimo',
        userValue: 'Valore utente',
        value: 'Valore',
      },
      noData: {
        message: 'Nessun valore disponibile da alcuna fonte per questa metrica.',
      },
      noDocument: {
        message: 'Nessun documento di riferimento disponibile per questa fonte.',
      },
      reference: {
        article: 'Articolo',
        articlePath: 'Percorso articolo',
        document: 'Documento',
        documentTitle: 'Titolo documento',
        pages: 'Pagina/e',
        viewPdf: 'Visualizza PDF',
      },
      report: {
        cancel: 'Annulla',
        contextTitle: 'Dettagli del valore',
        error: 'Invio della segnalazione fallito. Riprova.',
        explanation: 'Cosa non è corretto?',
        explanationPlaceholder: "Descrivi cosa è sbagliato e l'informazione corretta...",
        link: 'Segnala un valore errato',
        submit: 'Invia segnalazione',
        success: 'Segnalazione inviata con successo. Grazie!',
        suggestedValue: 'Valore corretto (opzionale)',
        suggestedValuePlaceholder: 'Inserisci il valore corretto',
        title: 'Segnala un valore errato',
      },
      sections: {
        additionalInfo: 'Informazioni aggiuntive',
        maxAdditionalInfo: 'Valore massimo - Informazioni aggiuntive',
        minAdditionalInfo: 'Valore minimo - Informazioni aggiuntive',
        referenceInfo: 'Informazioni di riferimento',
      },
      sources: {
        estimate: 'Stimato',
        geoportal: 'Geoportale',
        llm: 'Popety.io Research Lab',
        manual: 'Modifica manuale',
        old: 'Dati storici',
      },
      status: {
        notApplicable: 'Non applicabile',
        seeReference: 'Vedi riferimento',
      },
      titleDetails: 'Dettagli',
      titleRange: 'Intervallo',
      tooltips: {
        estimate: 'Stimato dagli algoritmi Popety.io',
        geoportal: 'Geoportale ufficiale',
        llm: 'Estratto dal regolamento',
        manual: "Sostituito dall'utente",
        old: 'Dati storici',
      },
      userOverride: {
        description: "Questo valore è stato modificato manualmente dall'utente. Clicca sulla scheda {{source}} per vedere i dettagli della fonte originale.",
        noAuthoritativeSource: 'Nessuna fonte autorevole (regolamento o geoportale) era disponibile per questa metrica.',
        timeline: {
          step1Desc: 'Fonte {{source}} : {{value}}',
          step1NoSource: 'Nessuna fonte originale disponibile',
          step1Title: "Valore stimato dall'algoritmo",
          step1TitleGeo: 'Valore dal geoportale',
          step1TitleReg: 'Valore dal regolamento',
          step2Desc: 'Il valore {{source}} ({{originalValue}}) è stato sostituito manualmente',
          step2DescNoSource: "Un valore manuale è stato impostato dall'utente",
          step2Title: "L'utente ha modificato il valore a {{value}}",
          step3Desc: 'Differenza: {{diff}} rispetto alla stima',
          step3DescNoOriginal: 'Valore impostato manualmente',
          step3Title: 'Il valore utilizzato è {{value}}',
          title: 'Come è stato definito questo valore?',
        },
      },
      warnings: {
        estimate: 'Questo valore è stato stimato utilizzando il nostro modello interno basato su proprietà simili nella zona. Si prega di notare che si tratta di una stima e potrebbe non riflettere il valore autorizzato effettivo. Per informazioni più accurate, consultare i documenti ufficiali di zonizzazione o contattare le autorità locali.',
        estimateShort: 'Questo valore è stato stimato dagli algoritmi di Popety.io tramite analisi spaziale delle parcelle nella stessa zona. Potrebbe differire dai documenti ufficiali di zonizzazione. Verificare sempre con le autorità locali.',
        geoportalShort: 'Questo valore proviene dal geoportale cantonale ufficiale. Possono verificarsi discrepanze se le normative sono state recentemente modificate. Si prega di verificare con i documenti ufficiali di zonizzazione o le autorità locali.',
        llm: 'Questo valore è stato ricercato e fornito dal laboratorio di ricerca di Popety.io utilizzando algoritmi avanzati e lavoro manuale. Sebbene ci impegniamo per la precisione, si prega di verificare queste informazioni con i documenti ufficiali di zonizzazione o le autorità locali per garantire la conformità.',
        regulationShort: 'Questo valore è stato ricercato e fornito dal laboratorio di ricerca di Popety.io utilizzando algoritmi avanzati e lavoro manuale. Sebbene ci impegniamo per la precisione, si prega di verificare con i documenti ufficiali di zonizzazione o le autorità locali.',
      },
    },
    pdfViewer: {
      errors: {
        defaultMessage: 'Si è verificato un errore durante il caricamento del documento PDF. Riprova.',
        loadFailed: 'Impossibile caricare il PDF',
        noUrl: 'Nessun URL PDF fornito',
        pageNotFound: 'Pagina non trovata',
        retry: 'Riprova',
        tryAgain: 'Riprova',
      },
      loading: {
        text: 'Caricamento del documento PDF...',
      },
      search: {
        closeSearch: 'Chiudi ricerca (Esc)',
        nextMatch: 'Corrispondenza successiva (Invio)',
        noMatches: 'Nessuna corrispondenza',
        noResults: 'Nessun risultato trovato',
        previousMatch: 'Corrispondenza precedente (Shift+Invio)',
        resultsCount: '{{current}} di {{total}}',
      },
      toolbar: {
        download: 'Scarica',
        downloadPdf: 'Scarica PDF',
        exitFullscreen: 'Esci dallo schermo intero',
        fullscreen: 'Schermo intero',
        nextPage: 'Pagina successiva',
        of: 'di',
        page: 'Pagina',
        previousPage: 'Pagina precedente',
        resetZoom: 'Ripristina zoom',
        rotateLeft: 'Ruota a sinistra',
        rotateRight: 'Ruota a destra',
        search: 'Cerca',
        searchPlaceholder: 'Cerca nel documento...',
        searchShortcut: 'Cerca (Ctrl+F)',
        zoomIn: 'Ingrandisci',
        zoomOut: 'Riduci',
      },
    },
    propertyAttributes: {
      categories: {
        amenities: 'Servizi e Caratteristiche',
        basic: 'Informazioni di base',
        condition: 'Condizione e Qualità',
        energy: 'Energia e Sistemi',
        environment: 'Ambiente e Posizione',
        physical: 'Caratteristiche fisiche',
      },
      empty: 'Nessun attributo di proprietà disponibile.',
      fields: {
        airConditioning: 'Aria condizionata',
        altitude: 'Altitudine',
        attic: 'Soffitta',
        balcony: 'Superficie del balcone',
        buildingHeight: "Altezza dell'edificio",
        cellar: 'Cantina',
        condition: 'Condizione generale',
        constructionYear: 'Anno di costruzione',
        distanceToPowerlines: 'Distanza dalle linee elettriche',
        elevator: 'Ascensore',
        energyClass: 'Classe energetica',
        exposureOrientation: 'Orientamento esposizione',
        facadeCondition: 'Condizione della facciata',
        fireplace: 'Camino',
        floor: 'Piano',
        floorArea: 'Superficie del piano',
        garage: 'Posti garage',
        garden: 'Superficie del giardino',
        heatConsumptionIndex: 'Indice di consumo termico',
        heatingSystemAge: 'Età del sistema di riscaldamento',
        heatingType: 'Tipo di riscaldamento',
        lakeView: 'Vista lago',
        landArea: 'Superficie del terreno',
        lastMaintenanceYear: 'Ultimo anno di manutenzione',
        livingArea: 'Superficie abitabile',
        minergie: 'Certificato Minergie',
        mountainView: 'Vista montagna',
        noiseLevel: 'Livello di rumore',
        numberOfBathrooms: 'Bagni',
        numberOfBedrooms: 'Camere da letto',
        numberOfFloors: 'Numero di piani',
        parking: 'Posti auto',
        pool: 'Piscina',
        propertyType: 'Tipo di proprietà',
        publicTransportQuality: 'Qualità trasporti pubblici',
        renovationYear: 'Anno di ristrutturazione',
        roofCondition: 'Condizione del tetto',
        roomsNb: 'Numero di stanze',
        sauna: 'Sauna',
        slope: 'Pendenza',
        solarArea: 'Superficie dei pannelli solari',
        solarPanelsInstalled: 'Pannelli solari installati',
        solarPotential: 'Potenziale solare',
        terrace: 'Superficie della terrazza',
        travelTimeToCenters: 'Tempo di viaggio ai centri',
        usableArea: 'Superficie utile',
        volume: 'Volume',
        windowsCondition: 'Condizione delle finestre',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        rooms: 'stanze',
        spaces: 'posti',
        squareMeters: 'm²',
        years: 'anni',
      },
    },
    propertyAttributesForm: {
      buttons: {
        cancel: 'Annulla',
        save: 'Salva modifiche',
        saving: 'Salvataggio...',
      },
      categories: {
        amenities: 'Servizi e Caratteristiche',
        basic: 'Informazioni di base',
        condition: 'Condizione e Qualità',
        energy: 'Energia e Sistemi',
        environment: 'Ambiente e Posizione',
        physical: 'Caratteristiche fisiche',
      },
      fields: {
        airConditioning: 'Aria condizionata',
        altitude: 'Altitudine',
        attic: 'Soffitta',
        balcony: 'Superficie del balcone',
        buildingHeight: "Altezza dell'edificio",
        cellar: 'Cantina',
        condition: 'Condizione generale',
        constructionYear: 'Anno di costruzione',
        distanceToPowerlines: 'Distanza dalle linee elettriche',
        elevator: 'Ascensore',
        energyClass: 'Classe energetica',
        exposureOrientation: 'Orientamento esposizione',
        facadeCondition: 'Condizione della facciata',
        fireplace: 'Camino',
        floor: 'Piano',
        floorArea: 'Superficie del piano',
        garage: 'Posti garage',
        garden: 'Superficie del giardino',
        heatConsumptionIndex: 'Indice di consumo termico',
        heatingSystemAge: 'Età del sistema di riscaldamento',
        heatingType: 'Tipo di riscaldamento',
        lakeView: 'Qualità vista lago',
        landArea: 'Superficie del terreno',
        lastMaintenanceYear: 'Ultimo anno di manutenzione',
        livingArea: 'Superficie abitabile',
        minergie: 'Certificato Minergie',
        mountainView: 'Qualità vista montagna',
        noiseLevel: 'Livello di rumore',
        numberOfBathrooms: 'Numero di bagni',
        numberOfBedrooms: 'Numero di camere da letto',
        numberOfFloors: 'Numero di piani',
        parking: 'Posti auto',
        pool: 'Piscina',
        propertyType: 'Tipo di proprietà',
        publicTransportQuality: 'Qualità trasporti pubblici',
        renovationYear: 'Anno di ristrutturazione',
        roofCondition: 'Condizione del tetto',
        roomsNb: 'Numero di stanze',
        sauna: 'Sauna',
        slope: 'Pendenza del terreno',
        solarArea: 'Superficie dei pannelli solari',
        solarPanelsInstalled: 'Pannelli solari installati',
        solarPotential: 'Potenziale solare',
        terrace: 'Superficie della terrazza',
        travelTimeToCenters: 'Tempo di viaggio ai centri',
        usableArea: 'Superficie utile',
        volume: 'Volume',
        windowsCondition: 'Condizione delle finestre',
      },
      options: {
        heatingType: {
          districtHeating: 'Teleriscaldamento',
          electricity: 'Elettricità',
          gas: 'Gas',
          heatPump: 'Pompa di calore',
          oil: 'Olio',
          other: 'Altro',
          pellets: 'Pellet',
          solarThermal: 'Solare termico',
          wood: 'Legna',
        },
        level: {
          high: 'Alto',
          low: 'Basso',
          medium: 'Medio',
        },
        quality: {
          bad: 'Cattivo',
          good: 'Buono',
          medium: 'Medio',
        },
        select: 'Seleziona...',
        viewQuality: {
          excellent: 'Eccellente',
          good: 'Buona',
          none: 'Nessuna',
          partial: 'Parziale',
        },
      },
      placeholders: {
        altitude: 'es. 450',
        balcony: 'es. 12',
        buildingHeight: 'es. 12',
        condition: 'es. eccellente, buono, discreto',
        constructionYear: 'es. 1985',
        distanceToPowerlines: 'es. 200',
        exposureOrientation: 'es. sud, sud-ovest',
        facadeCondition: 'es. eccellente, buono, discreto',
        floor: 'es. 3',
        floorArea: 'es. 100',
        garage: 'es. 1',
        garden: 'es. 200',
        heatConsumptionIndex: 'es. 80',
        heatingSystemAge: 'es. 10',
        landArea: 'es. 500',
        lastMaintenanceYear: 'es. 2020',
        livingArea: 'es. 120',
        numberOfBathrooms: 'es. 2',
        numberOfBedrooms: 'es. 3',
        numberOfFloors: 'es. 4',
        parking: 'es. 2',
        propertyType: 'es. appartamento, casa, villa',
        renovationYear: 'es. 2015',
        roofCondition: 'es. nuovo, buono, da riparare',
        roomsNb: 'es. 4.5',
        slope: 'es. pianeggiante, leggera, ripida',
        solarArea: 'es. 30',
        terrace: 'es. 25',
        travelTimeToCenters: 'es. 15 minuti dal centro città',
        usableArea: 'es. 140',
        volume: 'es. 450',
        windowsCondition: 'es. doppi vetri, nuovo',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        squareMeters: 'm²',
        years: 'anni',
      },
    },
    propertyDescriptionEditor: {
      buttons: {
        cancel: 'Annulla',
        edit: 'Modifica',
        editWithAi: 'Modifica con IA',
        save: 'Salva',
        saving: 'Salvataggio...',
      },
      characters: 'caratteri',
      emptyState: 'Clicca su "Modifica con IA" per generare rapidamente una descrizione ben strutturata e compilare automaticamente gli attributi della proprietà, oppure clicca su "Modifica" per aggiungerne una manualmente.',
      recommended: 'Consigliato: {{min}}-{{max}} caratteri',
      title: 'Descrizione della proprietà',
    },
    propertyDetails: {
      attributes: {
        empty: 'Nessun attributo di proprietà è stato ancora aggiunto.',
        title: 'Attributi',
      },
      buttons: {
        edit: 'Modifica',
        editWithAI: 'Modifica con IA',
        generateWithAI: 'Genera con IA',
        generating: 'Generazione...',
      },
      description: {
        empty: 'Nessuna descrizione è stata ancora aggiunta.',
        title: 'Descrizione della proprietà',
      },
      situationLocation: {
        empty: 'Nessuna descrizione di situazione e posizione è stata ancora aggiunta.',
        title: 'Situazione e Posizione',
      },
      subtitle: 'Descrizione dettagliata e informazioni sulla proprietà.',
      title: 'Dettagli proprietà',
    },
    reportModal: {
      cancel: 'Annulla',
      confirmDelete: 'Sei sicuro di voler eliminare questo report?',
      createNew: 'Crea nuovo report',
      delete: 'Elimina',
      edit: 'Modifica',
      noReports: 'Nessun report creato',
      sharedWithOffice: "Condiviso con l'ufficio",
      title: 'Report PDF',
      typeBuildingAnalysis: "Analisi dell'edificio",
      typeEstimate: 'Stima',
      typeLandAnalysis: 'Analisi fondiaria',
      typeLandGroupingAnalysis: 'Analisi di raggruppamento di particelle',
      untitledReport: 'Report senza titolo',
      updated: 'Aggiornato',
    },
    savedItems: {
      buildingPermits: {
        empty: 'Nessun permesso di costruire salvato',
        remove: 'Rimuovi dai salvati',
        title: 'Permessi di costruire salvati',
      },
      listings: {
        empty: 'Nessun annuncio salvato',
        remove: 'Rimuovi dai salvati',
        title: 'Annunci salvati',
      },
      transactions: {
        empty: 'Nessuna transazione salvata',
        remove: 'Rimuovi dai salvati',
        title: 'Transazioni salvate',
      },
    },
    tags: {
      addExistingTag: 'Aggiungi tag esistente',
      addSelectedTag: 'Aggiungi tag selezionato',
      addTags: 'Aggiungi tag',
      cancel: 'Annulla',
      createAndAdd: 'Crea e aggiungi',
      createNewTag: 'Crea nuovo tag',
      currentTags: 'Tag attuali',
      manageTitle: 'Gestisci tag',
      noAvailableTags: 'Nessun tag disponibile',
      noMatchingTags: 'Nessun tag corrispondente',
      noTagsYet: 'Nessun tag assegnato',
      primary: 'Primario',
      primaryConfirm: 'Il tag principale esistente sarà cambiato in secondario. Continuare?',
      primaryWarning: 'Il tag principale esistente diventerà secondario',
      searchPlaceholder: 'Cerca tag...',
      secondary: 'Secondario',
      setPrimary: 'Imposta come tag principale',
      tagNamePlaceholder: 'Nome del tag...',
    },
    tooltipMetric: {
      categories: 'Categorie:',
      howCalculated: 'Come viene calcolato:',
      whatItMeans: 'Cosa significa:',
      whyItMatters: 'Perché è importante:',
    },
  },
} satisfies AppTranslations
